import './style.scss';

import cn from 'classnames';
import { forwardRef, Fragment, ReactNode, useCallback } from 'react';

import { Tooltip } from '../../../ui';
import { copyToClipboard } from '../../../utils/clipboard';
import {
  parseNumberForCompactDisplay,
  shouldCompactNumber,
  ZeroSequenceSegment,
} from '../../utils/number';

import { NumberDisplayProps } from './NumberDisplay.types';

/**
 * React component for displaying numbers with compact zero sequences notation
 * Uses HTML <sub> elements with custom styling for subscript display
 * Compacts any sequence of consecutive zeros (leading, middle, or trailing)
 *
 * @example
 * <NumberDisplay value={123.0000456} /> // Renders: 123.0<sub>4</sub>456
 * <NumberDisplay value={0.0000456} />   // Renders: 0.0<sub>4</sub>456
 * <NumberDisplay value={0.01496550000001} /> // Renders: 0.01496550<sub>5</sub>1
 * <NumberDisplay value={123.045} />     // Renders: 123.045 (no compacting)
 */
export const NumberDisplay = forwardRef<HTMLSpanElement, NumberDisplayProps>(
  (
    {
      value,
      minZeroesCount = 3,
      showTooltip = true,
      decimalPlaces = 2,
      minDecimalPlaces = 2,
      className,
      nodeLeft,
      nodeRight,
      nodeLeftAfterSign = false,
      copyOnClick = false,
      onCopyValue,
      ...props
    },
    ref,
  ) => {
    const shouldCompact = shouldCompactNumber(value, minZeroesCount);

    // Handle click to copy
    const handleClick = useCallback(async () => {
      if (!copyOnClick) return;

      const stringValue = String(value);
      await copyToClipboard(stringValue);
      onCopyValue?.(stringValue);
    }, [copyOnClick, value, onCopyValue]);

    // Detect if value is negative for sign positioning
    const isNegative = typeof value === 'number' ? value < 0 : String(value).startsWith('-');
    const shouldExtractSign = nodeLeftAfterSign && isNegative;

    // Helper function to truncate decimal part
    const truncateDecimalPart = (decimalPart: string): string => {
      if (decimalPlaces <= 0) return '';
      return decimalPart.slice(0, decimalPlaces);
    };

    // Helper function to format integer part with thousands separators
    const formatIntegerPart = (intPart: string): string => {
      const isNeg = intPart.startsWith('-');
      const absInt = isNeg ? intPart.slice(1) : intPart;
      const formatted = absInt.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return isNeg ? `-${formatted}` : formatted;
    };

    // Render segments with subscripts for compacted zeros
    const renderSegments = (segments: ZeroSequenceSegment[]): ReactNode[] => {
      return segments.map((segment, index) => {
        if (segment.type === 'compact') {
          return (
            <Fragment key={index}>
              0<sub className="compact-number__subscript">{segment.zeroCount}</sub>
            </Fragment>
          );
        }
        return <Fragment key={index}>{segment.value}</Fragment>;
      });
    };

    // Render content without tooltip wrapper
    const renderContent = () => {
      if (!shouldCompact) {
        // Handle decimal places for non-compacted numbers
        const numStr = String(value);
        const dotIndex = numStr.indexOf('.');

        let displayValue = numStr;
        if (dotIndex !== -1) {
          const integerPart = numStr.substring(0, dotIndex);
          const decimalPart = numStr.substring(dotIndex + 1);
          const truncatedDecimal = truncateDecimalPart(decimalPart);
          const formattedInteger = formatIntegerPart(integerPart);

          displayValue = truncatedDecimal
            ? `${formattedInteger}.${truncatedDecimal}`
            : formattedInteger;
        } else if (minDecimalPlaces > 0) {
          // For integers, add decimal indicator to show precision
          displayValue = `${formatIntegerPart(numStr)}.${'0'.repeat(minDecimalPlaces)}`;
        } else {
          displayValue = formatIntegerPart(numStr);
        }

        // Remove sign from display value if we're extracting it
        const finalDisplayValue = shouldExtractSign ? displayValue.replace(/^-/, '') : displayValue;

        return (
          <span
            ref={ref}
            className={cn('compact-number', { 'compact-number--copyable': copyOnClick }, className)}
            onClick={handleClick}
            {...props}
          >
            {shouldExtractSign && '-'}
            {nodeLeft}
            {finalDisplayValue}
            {nodeRight}
          </span>
        );
      }

      // Parse number and get segments for compact display
      const { integerPart, segments } = parseNumberForCompactDisplay(value, minZeroesCount);

      // Apply decimalPlaces limit to the total decimal content
      // We need to track total characters and truncate accordingly
      let totalChars = 0;
      const truncatedSegments: ZeroSequenceSegment[] = [];

      for (const segment of segments) {
        if (totalChars >= decimalPlaces) break;

        if (segment.type === 'compact') {
          // Compact segments count as 1 visual character (the subscripted 0)
          if (totalChars + 1 <= decimalPlaces) {
            truncatedSegments.push(segment);
            totalChars += segment.zeroCount || 0;
          }
        } else {
          const remaining = decimalPlaces - totalChars;
          if (segment.value.length <= remaining) {
            truncatedSegments.push(segment);
            totalChars += segment.value.length;
          } else {
            truncatedSegments.push({
              type: 'text',
              value: segment.value.slice(0, remaining),
            });
            totalChars += remaining;
          }
        }
      }

      // Format integer part with thousands separators and handle sign extraction
      const formattedInteger = formatIntegerPart(integerPart);
      const finalIntegerPart = shouldExtractSign
        ? formattedInteger.replace(/^-/, '')
        : formattedInteger;

      return (
        <span
          ref={ref}
          className={cn('compact-number', { 'compact-number--copyable': copyOnClick }, className)}
          onClick={handleClick}
          {...props}
        >
          {shouldExtractSign && '-'}
          {nodeLeft}
          {finalIntegerPart}
          {truncatedSegments.length > 0 && '.'}
          {renderSegments(truncatedSegments)}
          {nodeRight}
        </span>
      );
    };

    // If tooltip is not needed or number is not compacted, return content as is
    if (!showTooltip || !shouldCompact) {
      return renderContent();
    }

    // Wrap content with tooltip
    return (
      <Tooltip
        inline
        trigger={renderContent()}
        content={String(value)}
        options={{ placement: 'top', offset: 5 }}
      />
    );
  },
);
